import {
  CircleAlertIcon,
  CircleCheckIcon,
  GitCompareIcon,
  MilestoneIcon,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import clsx from 'clsx';

import { Badge } from '@monksflow/monks-ui';

import type { CompetitorDto } from '@inference.engine/api-client';

import { AnalysisMarkdown } from '../../../../components';

import { getThreatConfig } from './utils';
import { useTranslation } from 'react-i18next';

interface CompetitorDetailProps {
  competitor: CompetitorDto;
  brandName?: string;
}

const boxClassName = 'flex h-full flex-col gap-3 rounded-lg border border-border-light p-xl';

/** The centred pill heading each box. Tone carries the judgement: a strength reads positive. */
const BoxChip = ({
  icon: Icon,
  tone = 'neutral',
  children,
}: {
  icon: LucideIcon;
  tone?: 'neutral' | 'positive' | 'destructive';
  children: ReactNode;
}) => (
  <span
    className={clsx(
      'inline-flex items-center gap-2 self-center rounded-full px-3.5 py-1.5 text-body-md font-medium whitespace-nowrap',
      tone === 'positive' && 'bg-positive text-positive-foreground',
      tone === 'destructive' && 'bg-destructive-soft text-destructive-soft-foreground',
      tone === 'neutral' && 'bg-surface text-secondary-foreground',
    )}
  >
    <Icon className="size-4 shrink-0" />
    {children}
  </span>
);

const BulletList = ({ items }: { items: string[] }) => (
  <ul className="flex flex-col gap-3">
    {items.map((item, i) => (
      <li key={`${item}-${i}`} className="flex items-start gap-3.5 pl-1.5">
        <span className="mt-[9px] size-1.5 shrink-0 rounded-full bg-foreground-subtle" />
        <AnalysisMarkdown text={item} className="text-body-md text-foreground-alt" />
      </li>
    ))}
  </ul>
);

export const CompetitorDetail = ({ competitor, brandName }: CompetitorDetailProps) => {
  const { t } = useTranslation('analysis');

  const threatConfig = getThreatConfig(competitor.threat);
  const hasStrengths = Boolean(competitor.strengths?.length);
  const hasWeaknesses = Boolean(competitor.weaknesses?.length);
  const hasMoves = Boolean(competitor.recentMoves?.length);

  return (
    <div className="flex flex-col gap-sm">
      {/* The position and its figures sit opposite the strategy they explain. */}
      <div className="grid grid-cols-1 items-start gap-16 p-6 lg:grid-cols-2">
        <div className="flex flex-col gap-5">
          {competitor.position && (
            <AnalysisMarkdown
              text={competitor.position}
              className="text-body-md text-foreground-alt"
            />
          )}

          <div className="flex w-full gap-12">
            {competitor.marketShare && (
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-body-md text-muted-foreground">
                  {t('analysisResult.competitorDetail.marketShare')}
                </span>
                <AnalysisMarkdown
                  text={competitor.marketShare}
                  className="text-body-xl font-medium text-foreground"
                />
              </div>
            )}
            {competitor.threat && (
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-body-md text-muted-foreground">
                  {t('analysisResult.competitorDetail.threatLevel')}
                </span>
                <Badge variant={threatConfig.variant} roundness="full">
                  {threatConfig.label}
                </Badge>
              </div>
            )}
          </div>
        </div>

        {competitor.strategy && (
          <div className="flex flex-col gap-3">
            <span className="text-label-md font-medium text-foreground-subtle">
              {t('analysisResult.competitorDetail.coreStrategy')}
            </span>
            <AnalysisMarkdown
              text={competitor.strategy}
              className="text-body-md text-foreground-alt"
            />
          </div>
        )}
      </div>

      {(hasStrengths || hasWeaknesses) && (
        <div className="grid grid-cols-1 items-stretch gap-sm lg:grid-cols-2">
          {hasStrengths && (
            <div className={boxClassName}>
              <BoxChip icon={CircleCheckIcon} tone="positive">
                {t('analysisResult.competitorDetail.strengths')}
              </BoxChip>
              <BulletList items={competitor.strengths ?? []} />
            </div>
          )}
          {hasWeaknesses && (
            <div className={boxClassName}>
              <BoxChip icon={CircleAlertIcon} tone="destructive">
                {t('analysisResult.competitorDetail.weaknesses')}
              </BoxChip>
              <BulletList items={competitor.weaknesses ?? []} />
            </div>
          )}
        </div>
      )}

      {(hasMoves || competitor.differentiation) && (
        <div className="grid grid-cols-1 items-stretch gap-sm lg:grid-cols-2">
          {hasMoves && (
            <div className={boxClassName}>
              <BoxChip icon={MilestoneIcon}>
                {t('analysisResult.competitorDetail.recentStrategicMoves')}
              </BoxChip>
              <BulletList items={competitor.recentMoves ?? []} />
            </div>
          )}
          {competitor.differentiation && (
            <div className={boxClassName}>
              <BoxChip icon={GitCompareIcon}>
                {brandName
                  ? t('analysisResult.competitorDetail.differentiationVs', { brandName })
                  : t('analysisResult.competitorDetail.differentiation')}
              </BoxChip>
              <AnalysisMarkdown
                text={competitor.differentiation}
                className="text-body-md text-foreground-alt"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
